<template>
  <Page
    ref="page"
    class="shortcut"
    :class="{
      'shortcut--disabled': hasClicked,
    }"
    :tabindex="tabIndex"
  >
    <!-- greeting -->
    <div class="page-content-anim">
      <SAM :data="greeting" class="page-content-margin" />
    </div>

    <!-- inbox -->
    <button
      type="button"
      class="shortcut-content-anim card-margin shortcut__button info info--black"
      @click="inboxClick"
      :disabled="hasClicked"
      :tabindex="tabIndex"
    >
      <span>{{ inboxLabel }}</span>
      <ImageAsset
        image="shortcut-inbox.svg"
        width="35"
        height="35"
        alt=""
        class="shortcut__icon"
        aria-hidden="true"
      />
    </button>

    <!-- story -->
    <button
      type="button"
      class="shortcut-content-anim card-margin shortcut__button info info--black"
      @click="storyClick"
      :disabled="hasClicked"
      :tabindex="tabIndex"
    >
      <span>{{ storyLabel }}</span>
      <ImageAsset
        image="shortcut-story.svg"
        width="35"
        height="35"
        alt=""
        class="shortcut__icon"
        aria-hidden="true"
      />
    </button>

    <!-- activity -->
    <button
      type="button"
      class="shortcut-content-anim card-margin shortcut__button info info--black"
      @click="activityClick"
      :disabled="hasClicked"
      :tabindex="tabIndex"
    >
      <span>{{ activityLabel }}</span>
      <ImageAsset
        image="shortcut-activity.svg"
        width="35"
        height="35"
        alt=""
        class="shortcut__icon"
        aria-hidden="true"
      />
    </button>

    <!-- logo -->
    <ChildFund class="shortcut__logo" />
  </Page>
</template>

<script>
import Page from "@/components/pages/Page.vue";
import SAM from "@/components/content/SAM.vue";
import ImageAsset from "@/components/content/image/ImageAsset.vue";
import ChildFund from "@/components/svg/ChildFund.vue";

import * as AudioManager from "@/scripts/audioManager.js";
import { trackEvent } from "@/scripts/analytics.js";
import { parse } from "@/scripts/insert.js";
import { data } from "@/scripts/data.js";

export default {
  components: {
    Page,
    SAM,
    ImageAsset,
    ChildFund,
  },
  data() {
    const {
      header_title,
      greeting,
      options: { activity, inbox, story },
    } = data.return.shortcut;

    return {
      hasClicked: false,

      headerTitle: header_title,
      greeting,
      inboxLabel: parse(inbox),
      activityLabel: parse(activity),
      storyLabel: parse(story),
    };
  },
  computed: {
    // disable interactive elements
    tabIndex() {
      return this.hasClicked ? "-1" : null;
    },
  },
  methods: {
    clickButton(name) {
      AudioManager.play("ui-input-button-select");
      this.hasClicked = true;

      this.goToShortcut(name);

      this.$root.setHeaderDisplay(false);
    },

    showShortcutHeader() {
      // update header state
      this.$root.setHeaderDisplay(true);
      this.$root.setHeaderTitle(this.headerTitle);
      this.$root.setHeaderProgressStyle("none");
    },

    // inbox
    inboxClick() {
      if (this.hasClicked) return;
      this.clickButton("inbox");
    },

    // story
    storyClick() {
      if (this.hasClicked) return;

      this.clickButton("story");
    },

    // activity
    activityClick() {
      if (this.hasClicked) return;
      this.clickButton("activity");
    },

    goToShortcut(shortcut) {
      let index = null;
      if (shortcut === "story") {
        index = 0;
      } else if (shortcut === "activity") {
        index = 1;
      }
      this.$root.goToShortcut(index);
    },
  },
  mounted() {
    trackEvent("open_shortcut");
    AudioManager.play("ui-menu-open");
    this.showShortcutHeader();
  },
  beforeUnmount() {
    trackEvent("close_shortcut");
  },
};
</script>

<style lang="scss">
.shortcut-content-anim {
  position: relative;

  @for $i from 1 through 10 {
    &:nth-child(#{$i}) {
      animation-delay: #{0.1s + ($i - 1) * 0.25s};
    }
  }

  animation: ease-up 1.25s $ease-out-quart both;
  @media (prefers-reduced-motion) {
    animation-name: ease-up-reduced-motion;
  }

  &::after {
    content: "";
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff;
    border-radius: inherit;
    box-shadow: 0 0 0.5rem #fff;

    animation: from-white 2.5s $ease-out-quart both;
    animation-delay: inherit;
  }
}

.shortcut {
  position: absolute;
  width: 100%;
  height: 100%;
  transform: translate3d(0, 0, 0); // this fixes the scroll somehow
  transform-origin: calc(50vw - min(300px, 42.5vw)) calc(100vh - 7.5vmin);

  &--disabled {
    pointer-events: none;
    overflow: hidden;
  }

  &__button {
    box-sizing: border-box;

    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    color: inherit;
    font-size: 1.5rem;
    font-weight: $bold;
    font-family: inherit;
    text-decoration: none;
    text-align: start;
    border: 0;
    cursor: pointer;

    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  &__icon {
    display: block;
    flex: 0 0 auto;
    height: 1.75rem;
    width: 1.75rem;
    object-fit: contain;
  }

  @keyframes shortcut-logo-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  &__logo {
    @for $i from 1 through 10 {
      &:nth-child(#{$i}) {
        animation-delay: #{0.1s + ($i - 1) * 0.25s};
      }
    }

    justify-self: flex-end;
    display: block;
    width: 10rem;
    max-width: 100%;
    height: auto;
    margin: $content-margin auto 0;
    opacity: 0;
    animation: shortcut-logo-in 0.5s ease-out both;
  }
}

@keyframes from-white {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes to-white {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes ease-up {
  0% {
    opacity: 0;
    transform: translate3d(0, 20vh, 0);
  }

  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes ease-up-reduced-motion {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

// shortcut anims

.shortcut-enter-active {
  transition: opacity 0.5s 0.5s ease-out, filter 0.5s 0.5s ease-out,
    transform 1s 0.5s $ease-out-quint;
}

.shortcut-leave-active {
  transition: opacity 0.5s ease-out, filter 0.5s ease-out,
    transform 0.5s ease-in;

  & > .page__inner > .shortcut-content-anim::after {
    animation: to-white 0.5s 0s $ease-out-quad both;
  }
}

.shortcut-enter-from {
  opacity: 0;
  filter: blur($blur);
  transform: scale(0.8);

  @media (prefers-reduced-motion) {
    transform: none;
  }
}

.shortcut-leave-to {
  opacity: 0;
  filter: blur($blur);
  transform: scale(0.9);

  @media (prefers-reduced-motion) {
    transform: none;
  }
}
</style>
